import { useState, useEffect } from "react";
import { Provider, defaultTheme, View } from "@adobe/react-spectrum";
import Dashboard from "../Dashboard";
import Loader from "../Loader/Loader";
import Login from "./Login";
import { connect, useSelector } from "react-redux";
import "./index.css";
import {
  SHOW_NOTIFICATION,
  HIDE_NOTIFICATION,
  HIDE_MEDIA_PERM_POPUP,
  SESSION_LOGOUT_MESSAGE,
  SESSION_LOGOUT_MESSAGE_CLOSE,
  CCP_ASK_MEDIA_PERMISSION,
  REACT_APP_AIC_USER_GUIDE_URL,
} from "../../constants/constants";
import Emitter from "../../emitter";
import storeRTK from "../../store/storeRTK";
import { setLoader } from "../../actions/common";
import { RootState } from "../../store/storeConfig";
import { registerAmazonConnectEvents } from "../../amazon-connect/handlers";
import registerEventHandlers from "../../controller";
import UnifiedDialogContainer from "../common/UnifiedDialogContainer";
import {
  checkMediaPermission,
  sendTestWebNotification,
  createNotificationPermissionNotification,
  testSoundBeep,
} from "../../utils/utils";

// Browser detection utility
function isSupportedBrowser() {
  const userAgent = navigator.userAgent.toLowerCase();
  return (userAgent.includes('chrome') && !userAgent.includes('edg') && !userAgent.includes('opr')) || 
         userAgent.includes('edg');
}

function mapStateToProps(state: any) {
  return {
    session: state.session,
    loggedIn: state.session.loggedIn,
  };
}

registerEventHandlers();
console.log("Event handlers registered!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!");
// Register Amazon Connect handlers
registerAmazonConnectEvents();

const LoginPage = (props: any) => {
  const [showPermPopup, setShowPermPopup] = useState(false);
  const [
    showNotificationPermissionDialog,
    setShowNotificationPermissionDialog,
  ] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState<any>({});
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showCCPPermPopup, setShowCCPPermPopup] = useState(false);
  const [showSoundDialog, setShowSoundDialog] = useState(false);
  const [soundState, setSoundState] = useState<
    "allow" | "deny" | "automatic" | "unknown"
  >("unknown");
  const ccpSessionState = useSelector(
    (state: RootState) => state.webrtc.ccpSessionState
  );
  const isLoading = useSelector((state: RootState) => state.common.isLoading);

  useEffect(() => {
    if (props.session?.isLoginProcessing) {
      storeRTK.dispatch(setLoader(true));
    } else {
      storeRTK.dispatch(setLoader(false));
    }

    // Check microphone permission
    checkMediaPermission((res: boolean) => {
      if (!res) {
        setShowPermPopup(true);
      }
    });

    if (
      Notification.permission === "default" ||
      Notification.permission === "denied"
    ) {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          setShowNotificationPermissionDialog(false);
          new Notification("Notifications enabled!");
        } else {
          setShowNotificationPermissionDialog(true);
          // TODO: Add a notification inside notification panel too
          new Notification("Notifications denied!");
        }
      });
    } else {
      setShowNotificationPermissionDialog(false);
    }

    // Show sound settings reminder dialog on login (once per browser session)
    // Only show on supported browsers (Chrome/Edge)
    // Chrome/Edge aggressively suspend AudioContext when browser is idle to save resources.
    // This causes ringtones to fail silently when contacts arrive after inactivity.
    // We show a helpful reminder dialog to help users configure optimal settings.
    // Note: We auto-resume suspended AudioContext before playing ringtones,
    // but users should still set "Allow" to avoid any browser restrictions entirely.
    if (isSupportedBrowser()) {
      const shownKey = "aic_sound_help_shown";
      const hasShown = sessionStorage.getItem(shownKey) === "true";
      
      if (!hasShown) {
        setShowSoundDialog(true);
        setSoundState("automatic"); // Set a default state for display
      }
    }

    const handleCCPAskMediaPermission = () => {
      setShowCCPPermPopup(true);
    };

    const handleShowNotification = (body: any) => {
      setShowNotification(true);
      setNotification(body);
    };

    const handleHideNotification = () => {
      setShowNotification(false);
      setNotification({});
    };

    const handleHideMediaPermPopup = () => {
      setShowPermPopup(false);
      setShowCCPPermPopup(false);
    };

    const handleSessionLogoutMessage = () => {
      setShowLogoutDialog(true);
    };

    const handleSessionLogoutMessageClose = () => {
      setShowLogoutDialog(false);
    };

    Emitter.on(CCP_ASK_MEDIA_PERMISSION, handleCCPAskMediaPermission);
    Emitter.on(SHOW_NOTIFICATION, handleShowNotification);
    Emitter.on(HIDE_NOTIFICATION, handleHideNotification);
    Emitter.on(HIDE_MEDIA_PERM_POPUP, handleHideMediaPermPopup);
    Emitter.on(SESSION_LOGOUT_MESSAGE, handleSessionLogoutMessage);
    Emitter.on(SESSION_LOGOUT_MESSAGE_CLOSE, handleSessionLogoutMessageClose);

    return () => {
      Emitter.off(CCP_ASK_MEDIA_PERMISSION, handleCCPAskMediaPermission);
      Emitter.off(SHOW_NOTIFICATION, handleShowNotification);
      Emitter.off(HIDE_NOTIFICATION, handleHideNotification);
      Emitter.off(HIDE_MEDIA_PERM_POPUP, handleHideMediaPermPopup);
      Emitter.off(SESSION_LOGOUT_MESSAGE, handleSessionLogoutMessage);
      Emitter.off(
        SESSION_LOGOUT_MESSAGE_CLOSE,
        handleSessionLogoutMessageClose
      );
      setShowLogoutDialog(false);
    };
  }, [
    props.session.loggedIn,
    props.session.isLoginProcessing,
    props.session.isReloginProcessing,
  ]);

  const openSettingsPage = () => {
    navigator.mediaDevices.getUserMedia({ audio: true }).then(() => {
      setShowPermPopup(false);
    });
    checkMediaPermission((res: boolean) => {
      if (res) {
        setShowPermPopup(false);
      }
    });
  };

  const openCCPPermissionTab = () => {
    checkMediaPermission((res: boolean) => {
      if (res) {
        setShowCCPPermPopup(false);
      }
    });
  };

  const handleNotificationPermissionOk = () => {
    if (Notification.permission === "default") {
      Notification.requestPermission().then((permission) => {
        console.log("Notification permission result:", permission);
        // Dispatch notification to panel
        createNotificationPermissionNotification(permission);
        setShowNotificationPermissionDialog(false);
      });
    } else {
      // Dispatch notification to panel for current permission state
      createNotificationPermissionNotification(Notification.permission);
      setShowNotificationPermissionDialog(false);
    }
  };

  const handleOnPress = () => {
    setShowNotification(false);
  };

  const handleOnPressLogoutPopup = () => {
    setShowLogoutDialog(false);
  };

  const handleSendTestNotification = () => {
    sendTestWebNotification();
  };

  const handleTestSound = async () => {
    await testSoundBeep();
  };

  const handleSoundDialogClose = () => {
    const shownKey = "aic_sound_help_shown";
    sessionStorage.setItem(shownKey, "true");
    setShowSoundDialog(false);
  };

  // // Make test notification function available globally for debugging
  // (window as any).testWebNotification = sendTestWebNotification;

  return (
    <Provider theme={defaultTheme} colorScheme="light">
      <View height={"100vh"} width={"100vw"}>
        <View height={"100%"}>
          <View height={"100%"}>
            {!props.session.loggedIn &&
              !props.session.isLoginProcessing &&
              !props.session.isReloginProcessing && <Login />}
            {(props.session.loggedIn ||
              props.session.isLoginProcessing ||
              props.session.isReloginProcessing) && <Dashboard />}
            {
              <View>
                {isLoading ? (
                  <Loader checkLoading="loading" />
                ) : ccpSessionState !== 4 &&
                  ccpSessionState !== 3 &&
                  props.session.loggedIn ? (
                  <Loader checkLoading="webrtc" />
                ) : null}
              </View>
            }
          </View>

          <UnifiedDialogContainer
            showPermPopup={showPermPopup}
            onMicContinue={openSettingsPage}
            showNotificationPermissionDialog={showNotificationPermissionDialog}
            onNotificationTest={handleSendTestNotification}
            onNotificationAcknowledge={handleNotificationPermissionOk}
            showSoundDialog={showSoundDialog}
            soundState={soundState}
            onSoundTest={handleTestSound}
            onSoundClose={handleSoundDialogClose}
            showCCPPermPopup={showCCPPermPopup}
            onCCPAllow={openCCPPermissionTab}
            showLogoutDialog={showLogoutDialog}
            onLogoutOk={handleOnPressLogoutPopup}
            showNotification={showNotification}
            notification={notification}
            onNotificationClose={handleOnPress}
            userGuideUrl={
              process.env.REACT_APP_AIC_USER_GUIDE_URL ||
              REACT_APP_AIC_USER_GUIDE_URL
            }
          />
        </View>
      </View>
    </Provider>
  );
};

export default connect(mapStateToProps)(LoginPage);
